import { GoogleOutlined } from "@ant-design/icons";
import { Typography, message, Input, Button, Form } from "antd";
import { sendPasswordResetEmail } from "firebase/auth";
import { useEffect, useRef, useState } from "react";
import {
  useLoaderData,
  useFetcher,
  useSearchParams,
  useNavigation,
} from "react-router-dom";
import { auth } from "~/lib";
import { INTENTS } from "~/utils";
import { SignUpPageLoaderType } from "./loaders";
import { useAppTranslation } from "~/hooks/useAppTranslation";

const { Title, Text } = Typography;

export const SignUpPage = () => {
  const _loaderData = useLoaderData() as SignUpPageLoaderType;
  const { t } = useAppTranslation();
  const [isSignUp, setIsSignUp] = useState(true);
  const fetcher = useFetcher();
  const lastFetcherDataRef = useRef<unknown>(undefined);
  const [searchParams] = useSearchParams();
  const returnUrl = searchParams.get("returnUrl") || "";
  const navigation = useNavigation();
  const isPageLoading = navigation.state !== "idle";
  const submitting = fetcher.state !== "idle";

  const [form] = Form.useForm();

  useEffect(() => {
    if (fetcher.state !== "idle") return;
    if (fetcher.data === undefined || fetcher.data === null) return;
    if (fetcher.data === lastFetcherDataRef.current) return;
    lastFetcherDataRef.current = fetcher.data;
    const data = fetcher.data as { error?: string };
    if (typeof data.error === "string" && data.error.length > 0) {
      message.error(data.error, 8);
    }
  }, [fetcher.state, fetcher.data]);

  const handleFormToggle = () => {
    setIsSignUp(!isSignUp);
  };

  const handleGoogleLogin = async () => {
    fetcher.submit(
      {
        intent: isSignUp ? INTENTS._google_signup : INTENTS._google_signin,
        returnUrl: returnUrl,
      },
      { method: "post" }
    );

    form.resetFields();
  };

  const handleSubmit = async (values: Record<string, unknown>) => {
    fetcher.submit(
      {
        intent: isSignUp ? INTENTS._signup : INTENTS._signin,
        body: JSON.stringify(values),
        returnUrl: returnUrl,
      },
      { method: "post" }
    );
  };

  const handleForgotPassword = async () => {
    try {
      const email = form.getFieldValue("email");
      await sendPasswordResetEmail(auth, email || "");
      message.success(t("Password_reset_email_sent"));
    } catch (error) {
      message.error(t("Password_reset_email_failed"));
    }
  };

  return (
    <div className="signup-form">
      <Title className="signup-form-title" level={1}>
        {isSignUp ? t("Sign_up") : t("Sign_in")}
      </Title>
      <Form form={form} onFinish={handleSubmit}>
        {!isSignUp && (
          <>
            <Form.Item
              className="signup-form-item"
              label={t("Email")}
              name="email"
              rules={[
                {
                  required: true,
                  message: t("Please_enter_your_email"),
                },
                {
                  type: "email",
                  message: t("Please_enter_a_valid_email"),
                },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              className="signup-form-item"
              label={t("Password")}
              name="password"
              rules={[
                {
                  required: true,
                  message: t("Please_enter_your_password"),
                },
              ]}
            >
              <Input.Password />
            </Form.Item>
            <Form.Item className="signup-form-forgot-password">
              <Button type="link" onClick={handleForgotPassword}>
                {t("Forgot_password")}
              </Button>
            </Form.Item>
          </>
        )}
        {isSignUp && (
          <>
            <Form.Item
              className="signup-form-item"
              label={t("First_Name")}
              name="firstName"
              rules={[
                {
                  required: true,
                  message: t("Please_enter_your_first_name"),
                },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              className="signup-form-item"
              label={t("Last_Name")}
              name="lastName"
              rules={[
                {
                  required: true,
                  message: t("Please_enter_your_last_name"),
                },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              className="signup-form-item"
              label={t("Email")}
              name="email"
              rules={[
                {
                  required: true,
                  message: t("Please_enter_your_email"),
                },
                {
                  type: "email",
                  message: t("Please_enter_a_valid_email"),
                },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              className="signup-form-item"
              label={t("Username")}
              name="username"
              rules={[
                {
                  required: true,
                  message: t("Please_enter_your_username"),
                },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              className="signup-form-item"
              label={t("Password")}
              name="password"
              rules={[
                {
                  required: true,
                  message: t("Please_enter_your_password"),
                },
              ]}
            >
              <Input.Password />
            </Form.Item>
          </>
        )}
        <Form.Item className="signup-form-submit-button">
          <Button
            type="primary"
            htmlType="submit"
            loading={submitting}
            disabled={isPageLoading || submitting}
            block
          >
            {isSignUp ? t("Sign_up") : t("Sign_in")}
          </Button>
        </Form.Item>
        <Form.Item className="signin-with-google">
          <Button
            type="primary"
            onClick={handleGoogleLogin}
            icon={<GoogleOutlined />}
            disabled={isPageLoading || submitting}
          >
            Google {isSignUp ? t("Sign_up") : t("Sign_in")}
          </Button>
        </Form.Item>
        <Form.Item className="signup-form-toggle">
          <Text>
            {isSignUp
              ? t("Already_have_an_account")
              : t("Dont_have_an_account")}
            <Button
              type="link"
              onClick={handleFormToggle}
              disabled={isPageLoading || submitting}
            >
              {isSignUp ? t("Sign_in") : t("Sign_up")}
            </Button>
          </Text>
        </Form.Item>
      </Form>
    </div>
  );
};
